import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MiniGameService } from '../../../_services/mini-game.service';
import { MiniGameSession, MiniGameStudent, BombPassState } from '../../../interfaces/mini-game.interface';
import { GameQuestion } from '../../../interfaces/question.interface';
import { GlobalService } from 'src/app/_services/global.service';
import { GameAudioService } from '../../../_services/game-audio.service';

type BombPhase = 'holder-show' | 'question' | 'result' | 'explosion';

@Component({
  selector: 'app-bomb-pass-game',
  templateUrl: './bomb-pass-game.component.html',
  styleUrls: ['./bomb-pass-game.component.scss']
})
export class BombPassGameComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  currLang: any = 'en';

  session: MiniGameSession | null = null;
  currentQuestion: GameQuestion | null = null;
  selectedOption: 'A' | 'B' | 'C' | 'D' | null = null;
  internalPhase: BombPhase = 'holder-show';
  lastResult: { isCorrect: boolean; xpEarned: number } | null = null;
  isExploding = false;
  timerValue = 0;
  private explosionAudio: HTMLAudioElement | null = null;
  private sparkleAudio: HTMLAudioElement | null = null;
  private correctSound = new Audio('assets/game-mode/sound-effects/correctSoundEffect.mp3');
  private pendingTimeouts: any[] = [];
  private destroyed = false;
  showInfo = false;

  get state(): BombPassState | null {
    return this.session?.gameState as BombPassState || null;
  }

  get currentHolder(): MiniGameStudent | null {
    if (!this.session || !this.state) return null;
    return this.session.students.find(s => s.id === this.state!.currentHolderId) || null;
  }

  constructor(
    private miniGameService: MiniGameService,
    private globalService: GlobalService,
    private gameAudioService: GameAudioService
  ) {}

  ngOnInit(): void {
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => this.currLang = lang);

    this.miniGameService.session$
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        this.session = session;
        if (session) {
          this.currentQuestion = session.questions[session.currentQuestionIndex] || null;
        }
      });

    this.miniGameService.lastResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => this.lastResult = result);

    this.miniGameService.timerValue$
      .pipe(takeUntil(this.destroy$))
      .subscribe(val => this.timerValue = val);

    this.miniGameService.timerExpired$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.internalPhase === 'question') {
          this.onBombExploded();
        }
      });

    this.showHolder();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.pendingTimeouts.forEach(t => clearTimeout(t));
    this.pendingTimeouts = [];
    this.stopSparkleSound();
    this.gameAudioService.stop(this.explosionAudio);
    this.gameAudioService.stop(this.correctSound);
    this.destroy$.next();
    this.destroy$.complete();
    this.miniGameService.stopTimer();
  }

  private safeTimeout(fn: () => void, ms: number): void {
    const id = setTimeout(() => {
      if (!this.destroyed) fn();
    }, ms);
    this.pendingTimeouts.push(id);
  }

  showHolder(): void {
    this.internalPhase = 'holder-show';
    this.isExploding = false;
    if (this.currentHolder) {
      this.miniGameService.setCurrentStudent(this.currentHolder.id);
    }

    this.safeTimeout(() => {
      this.internalPhase = 'question';
      this.selectedOption = null;
      const fuseTime = this.state?.fuseTime || 10;
      this.miniGameService.startTimer(fuseTime);
      this.playSparkleSound();
    }, 3500);
  }

  selectOption(optionId: 'A' | 'B' | 'C' | 'D'): void {
    this.selectedOption = optionId;
  }

  onBombExploded(): void {
    // Timer ran out — bomb explodes on current holder
    this.miniGameService.submitAnswer(null, {
      studentId: this.currentHolder?.id
    });
    this.triggerExplosion();
  }

  submitAnswer(): void {
    if (!this.selectedOption) return;
    this.miniGameService.submitAnswer(this.selectedOption, {
      studentId: this.currentHolder?.id
    });

    const isCorrect = this.lastResult?.isCorrect ?? false;

    if (!isCorrect) {
      this.triggerExplosion();
    } else {
      this.stopSparkleSound();
      this.gameAudioService.playExisting(this.correctSound);
      this.internalPhase = 'result';
      this.safeTimeout(() => this.advanceToNext(), 1500);
    }
  }

  private triggerExplosion(): void {
    this.stopSparkleSound();
    this.isExploding = true;
    this.internalPhase = 'explosion';
    this.playExplosionSound();
    this.safeTimeout(() => this.advanceToNext(), 2500);
  }

  private playSparkleSound(): void {
    this.stopSparkleSound();
    this.sparkleAudio = this.gameAudioService.play('assets/game-mode/sound-effects/bompSparkleSoundEffect.mp3', { loop: true });
  }

  private stopSparkleSound(): void {
    this.gameAudioService.stop(this.sparkleAudio);
    this.sparkleAudio = null;
  }

  private playExplosionSound(): void {
    this.gameAudioService.stop(this.explosionAudio);
    this.explosionAudio = this.gameAudioService.play('assets/game-mode/sound-effects/bombExplodedSoundEffect.mp3');
  }

  private advanceToNext(): void {
    this.isExploding = false;
    this.miniGameService.nextQuestion();
    if (this.session && this.session.currentQuestionIndex < this.session.questions.length) {
      this.currentQuestion = this.session.questions[this.session.currentQuestionIndex] || null;
      this.showHolder();
    }
  }

  getQuestionNum(): number {
    return (this.session?.currentQuestionIndex || 0) + 1;
  }

  getTotalQuestions(): number {
    return this.session?.questions.length || 10;
  }

  getHolderName(): string {
    if (!this.currentHolder) return '';
    return this.currLang === 'ar' ? this.currentHolder.name.ar : this.currentHolder.name.en;
  }

  quitGame(): void {
    this.miniGameService.finishMiniGame();
  }

  getExplosionCount(): number {
    return this.state?.explosions.length || 0;
  }

  isStudentExploded(studentId: number): boolean {
    return this.state?.explosions?.some(e => e.studentId === studentId) || false;
  }

  getStudentShortName(student: MiniGameStudent): string {
    const fullName = this.currLang === 'ar' ? student.name?.ar : student.name?.en;
    if (!fullName) return '';
    const firstName = fullName.split(' ')[0];
    return firstName.length > 7 ? firstName.slice(0, 6) + '.' : firstName;
  }
}
